// src/pages/Enseignants/PassagesGrades/components/GradeNoteDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaCheckCircle, FaSave, FaTimes, FaTrashAlt } from "react-icons/fa";

import {
  createGradePassageLine,
  formatDateFr,
  getAdherentFullName,
  parseGradePassage,
  removeGradePassageLine,
  todayISO,
  upsertGradePassageLine,
  GRADE_MARKER,
} from "../utils/gradePassageUtils";

const GradeNoteDrawer = ({
  isOpen,
  adherent,
  teacherName,
  saving,
  onClose,
  onSave,
}) => {
  const [gradeDate, setGradeDate] = useState(todayISO());
  const [result, setResult] = useState("Obtenu");
  const [comment, setComment] = useState("");
  const [notesText, setNotesText] = useState("");
  const [trackingRemoved, setTrackingRemoved] = useState(false);

  const gradeInfo = useMemo(
    () => parseGradePassage(adherent?.notes_enseignant),
    [adherent],
  );

  useEffect(() => {
    if (!adherent) return;

    const info = parseGradePassage(adherent?.notes_enseignant);

    setGradeDate(info.date || todayISO());
    setResult(info.result || "Obtenu");
    setComment(info.comment || "");
    setNotesText(adherent?.notes_enseignant || "");
    setTrackingRemoved(false);
  }, [adherent]);

  if (!isOpen || !adherent) return null;

  const fullName = getAdherentFullName(adherent);

  const previewLine = createGradePassageLine({
    date: gradeDate,
    result,
    teacher: teacherName,
    comment,
  });

  const handleApplyLine = () => {
    setTrackingRemoved(false);

    setNotesText((current) =>
      upsertGradePassageLine(current, {
        date: gradeDate,
        result,
        teacher: teacherName,
        comment,
      }),
    );
  };

  const handleRemoveLine = () => {
    setTrackingRemoved(true);
    setNotesText((current) => removeGradePassageLine(current));
  };

  const handleSave = () => {
    if (trackingRemoved) {
      onSave(removeGradePassageLine(notesText));
      return;
    }

    const hasGradeLine = String(notesText || "").includes(GRADE_MARKER);

    const finalNotes = hasGradeLine
      ? upsertGradePassageLine(notesText, {
          date: gradeDate,
          result,
          teacher: teacherName,
          comment,
        })
      : upsertGradePassageLine(notesText, {
          date: gradeDate,
          result,
          teacher: teacherName,
          comment,
        });

    onSave(finalNotes);
  };

  const labelClass =
    "mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

  const fieldClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-blue-400";

  return (
    <div className="fixed inset-0 z-[80]">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Fermer"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="border-b border-slate-200 bg-slate-950 px-5 py-5 text-white dark:border-slate-800">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-200">
                Passage de grade
              </p>
              <h2 className="mt-1 text-xl font-bold">{fullName}</h2>
              <p className="mt-1 text-sm text-slate-300">
                Enseignant : {teacherName}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
            {gradeInfo.isGraded ? (
              <div className="flex gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <FaCheckCircle />
                </div>

                <div>
                  <p className="font-bold text-slate-900 dark:text-white">
                    Déjà noté le {formatDateFr(gradeInfo.date)}
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Résultat : {gradeInfo.result || "non renseigné"} · Par{" "}
                    {gradeInfo.teacher || "enseignant non renseigné"}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <p className="font-bold text-slate-900 dark:text-white">
                  Cet adhérent n’est pas encore noté.
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Renseignez la date, le résultat puis enregistrez.
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Date de passage</label>
              <input
                type="date"
                className={fieldClass}
                value={gradeDate}
                onChange={(event) => setGradeDate(event.target.value)}
              />
            </div>

            <div>
              <label className={labelClass}>Résultat</label>
              <select
                className={fieldClass}
                value={result}
                onChange={(event) => setResult(event.target.value)}
              >
                <option value="Obtenu">Obtenu</option>
                <option value="Non obtenu">Non obtenu</option>
                <option value="Noté">Noté, résultat à préciser</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Commentaire optionnel</label>
            <input
              type="text"
              className={fieldClass}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Ex : Doit revoir le vocabulaire, très bon travail..."
            />
          </div>

          <div className="rounded-[24px] border border-blue-100 bg-blue-50 p-4 dark:border-blue-500/20 dark:bg-blue-500/10">
            <p className="text-xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">
              Ligne générée
            </p>
            <p className="mt-2 break-words rounded-2xl bg-white p-3 text-sm font-medium text-slate-700 dark:bg-slate-950 dark:text-slate-200">
              {previewLine}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleApplyLine}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-blue-700"
              >
                Appliquer dans le texte
              </button>

              <button
                type="button"
                onClick={handleRemoveLine}
                className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-white px-4 py-2.5 text-xs font-bold text-rose-700 transition hover:bg-rose-50 dark:border-rose-500/20 dark:bg-slate-950 dark:text-rose-300 dark:hover:bg-rose-500/10"
              >
                <FaTrashAlt />
                Retirer le suivi
              </button>
              {trackingRemoved && (
                <p className="mt-3 rounded-2xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
                  Le suivi de passage sera supprimé après enregistrement.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Texte complet notes_enseignant</label>
            <textarea
              className={`${fieldClass} min-h-56 resize-y leading-6`}
              value={notesText}
              onChange={(event) => setNotesText(event.target.value)}
              placeholder="Notes enseignant..."
            />
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              À l’enregistrement, la ligne de suivi passage de grade sera mise à
              jour automatiquement au début du champ.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white px-5 py-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
            >
              <FaSave className={saving ? "animate-pulse" : ""} />
              Enregistrer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default GradeNoteDrawer;
